import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ClipboardCheck, ChevronRight, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import VelocityCard from "@/components/VelocityCard";

const PHASE_META = {
  mid_year: { label: "Mid-Year", color: "bg-blue-50 text-blue-700 border-blue-200" },
  year_end: { label: "Year-End", color: "bg-violet-50 text-violet-700 border-violet-200" },
};

const STATUS_META = {
  self_pending: { label: "Self-assessment due", style: "bg-amber-50 text-amber-700 border-amber-200" },
  manager_pending: { label: "Manager review", style: "bg-blue-50 text-blue-700 border-blue-200" },
  joint_pending: { label: "Joint sign-off", style: "bg-violet-50 text-violet-700 border-violet-200" },
  signed_off: { label: "Signed off", style: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  calibrated: { label: "Calibrated", style: "bg-slate-100 text-slate-700 border-slate-200" },
};

export default function Reviews() {
  const { user } = useAuth();
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCycles, setActiveCycles] = useState([]);
  const [acting, setActing] = useState(false);
  const isAdmin = hasRole(user, "tenant_admin", "hr_admin");
  const isHR = hasRole(user, "super_admin", "tenant_admin", "hr_admin", "hrbp");
  const [velocity, setVelocity] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/reviews", { params: { role: "self" } }),
        api.get("/reviews", { params: { role: "manager" } }),
        api.get("/cycles"),
      ]);
      setMine(a.data.items || []);
      setTeam(b.data.items || []);
      setActiveCycles((c.data.items || []).filter((x) => x.status === "active"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    if (!isHR) return;
    api.get("/reviews/velocity").then((r) => setVelocity(r.data)).catch(() => setVelocity(null));
  }, [isHR]);

  const openMidYear = async (cycleId) => {
    setActing(true);
    try {
      const r = await api.post(`/reviews/spawn/${cycleId}/mid_year`);
      toast.success(`Mid-year phase opened · ${r.data.created} reviews created`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setActing(false);
    }
  };

  const noReviewsYet = !mine.length && !team.length;

  return (
    <div className="max-w-6xl space-y-6" data-testid="reviews-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="axia-overline">Performance</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Reviews</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">Mid-year and year-end reviews. Self-assessment → manager review → joint sign-off.</p>
        </div>
        {isAdmin && noReviewsYet && activeCycles.length > 0 && (
          <Button onClick={() => openMidYear(activeCycles[0].id)} disabled={acting} variant="outline" data-testid="spawn-midyear-btn">
            {acting ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-2" />}
            Open mid-year phase
          </Button>
        )}
      </div>

      {isHR && velocity && (
        <VelocityCard
          velocity={velocity}
          overline="Review velocity · last 8 weeks"
          unit="submission"
          unitPlural="submissions"
          primaryColor="#3b82f6"
          testId="reviews-velocity-card"
          totalsLabel={({ total }) => `8wk total: ${total} submission${total === 1 ? "" : "s"} & sign-offs`}
          interpret={(v) => {
            if (v.total === 0) return "No reviews submitted yet this period.";
            if (v.delta_wow > 0) return "Reviews moving forward — managers and employees are keeping up.";
            const last = v.weeks[v.weeks.length - 2];
            if (v.delta_wow < 0 && last && last.count > 0) return "Submission pace slowed this week. Stuck reviewers may need a nudge.";
            return "Steady review submission cadence.";
          }}
        />
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : noReviewsYet ? (
        <div className="axia-card p-12 text-center text-slate-500" data-testid="no-reviews-state">
          <ClipboardCheck className="w-7 h-7 mx-auto text-slate-300" />
          <h2 className="font-display text-lg font-semibold text-slate-900 mt-3">No active reviews</h2>
          <p className="text-sm mt-1">
            {isAdmin
              ? "Open the mid-year or year-end phase on a cycle to spawn reviews for all participants."
              : "When HR opens the next review phase, your review will appear here."}
          </p>
        </div>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="mine" data-testid="tab-mine">My reviews ({mine.length})</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team">Team reviews ({team.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="mine" className="pt-4 space-y-3">
            {mine.length === 0 ? (
              <div className="text-sm text-slate-500">No reviews of yours.</div>
            ) : mine.map((r) => <ReviewCard key={r.id} review={r} role="self" />)}
          </TabsContent>
          <TabsContent value="team" className="pt-4 space-y-3">
            {team.length === 0 ? (
              <div className="text-sm text-slate-500">No team reviews to action.</div>
            ) : team.map((r) => <ReviewCard key={r.id} review={r} role="manager" />)}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function ReviewCard({ review, role }) {
  const phase = PHASE_META[review.phase];
  const status = STATUS_META[review.status];
  const subjectName = role === "self" ? "You" : review.employee_name;

  return (
    <Link
      to={`/reviews/${review.id}`}
      className="block axia-card p-5 hover:border-slate-400 transition"
      data-testid={`review-card-${review.id}`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`axia-pill ${phase.color}`}>{phase.label}</span>
            <span className={`axia-pill ${status.style}`}>{status.label}</span>
          </div>
          <div className="mt-2 font-display text-base font-semibold text-slate-900">
            {role === "self" ? "Your review" : `Review for ${subjectName}`}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {(review.objective_assessments || []).length} objectives · {(review.value_assessments || []).length} values
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </div>
    </Link>
  );
}
